import React from 'react';

/* ── Helpers ──────────────────────────────────────────────────── */
const MODE_COLORS = {
  classic:  { color:'#3b82f6', label:'Classic'   },
  popular:  { color:'#f97316', label:'Popular'   },
  quick:    { color:'#00d4ff', label:'Quick'     },
  timer:    { color:'#06b6d4', label:'Timer'     },
  reverse:  { color:'#f59e0b', label:'Reverse'   },
  token_1:  { color:'#10b981', label:'1-Token'   },
  token_2:  { color:'#10b981', label:'2-Token'   },
  token_3:  { color:'#10b981', label:'3-Token'   },
  no_cut:   { color:'#8b5cf6', label:'No Cut'    },
  team_2v2: { color:'#ec4899', label:'2v2 Team'  },
};

function PlayerPips({ filled, total }) {
  return (
    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: 9, height: 9, borderRadius: '50%',
          background: i < filled ? '#00d4ff' : 'rgba(255,255,255,0.15)',
          boxShadow: i < filled ? '0 0 5px rgba(0,212,255,0.6)' : 'none',
          transition: 'background 0.2s',
        }} />
      ))}
      <span style={{ color:'rgba(255,255,255,0.5)', fontSize:11, marginLeft:4 }}>{filled}/{total}</span>
    </div>
  );
}

function WaitingBadge() {
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      background:'rgba(0,200,83,0.12)', color:'#00c853',
      border:'1px solid rgba(0,200,83,0.3)',
      padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700,
      letterSpacing:'0.4px', textTransform:'uppercase',
    }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:'#00c853', boxShadow:'0 0 5px #00c853', display:'inline-block' }} />
      Open
    </span>
  );
}

function RunningBadge() {
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      background:'rgba(0,212,255,0.10)', color:'#00d4ff',
      border:'1px solid rgba(0,212,255,0.25)',
      padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700,
      letterSpacing:'0.4px', textTransform:'uppercase',
    }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:'#00d4ff', animation:'pulse 1.2s infinite', display:'inline-block' }} />
      Live
    </span>
  );
}

/* ── Room card (waiting) ──────────────────────────────────────── */
function WaitingRoomCard({ room, onJoin }) {
  const filled   = room.players?.length ?? 0;
  const total    = room.maxPlayers || 4;
  const modeMeta = MODE_COLORS[room.gameMode] || MODE_COLORS.classic;

  return (
    <div
      style={{
        display:'flex', flexDirection:'column', gap:12,
        background:'rgba(13,19,50,0.6)',
        border:'1px solid rgba(255,255,255,0.08)',
        borderRadius:14,
        padding:'14px 16px',
        transition:'border-color 0.2s, box-shadow 0.2s',
        position:'relative',
        overflow:'hidden',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,212,255,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Subtle left accent */}
      <div style={{ position:'absolute', left:0, top:12, bottom:12, width:3, borderRadius:'0 3px 3px 0', background:modeMeta.color, opacity:0.7 }} />

      {/* Top row */}
      <div style={{ display:'flex', alignItems:'center', gap:10, paddingLeft:8 }}>
        {/* Lock icon */}
        {room.private && (
          <span title="Private room" style={{ fontSize:14, flexShrink:0 }}>🔒</span>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:'#fff', fontWeight:700, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {room.name || 'Game Room'}
          </div>
          <div style={{ color:'rgba(255,255,255,0.35)', fontSize:11, marginTop:1 }}>
            ROOM ID: {room.roomCode || room._id?.toString().slice(-8).toUpperCase()}
          </div>
        </div>
        <WaitingBadge />
      </div>

      {/* Meta row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, paddingLeft:8 }}>
        <PlayerPips filled={filled} total={total} />
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <span style={{
            background:`${modeMeta.color}22`,
            border:`1px solid ${modeMeta.color}44`,
            borderRadius:6, padding:'2px 8px',
            fontSize:10, fontWeight:600, color:modeMeta.color,
          }}>{modeMeta.label}{room.gameMode === 'timer' ? ` • ${room.timerMinutes || 2}m` : ''}</span>
          {room.gameMode === 'timer' && (
            <span style={{ background:'rgba(6,182,212,0.15)', border:'1px solid rgba(103,232,249,0.30)', borderRadius:6, padding:'2px 8px', fontSize:10, fontWeight:700, color:'#67e8f9' }}>
              ⏱ {room.timerMinutes || 2}m
            </span>
          )}
        </div>
        <button
          onClick={() => onJoin(room)}
          style={{
            padding:'7px 18px', borderRadius:8,
            background:'linear-gradient(135deg, #00d4ff, #0090e7)',
            color:'#060b1f', fontSize:13, fontWeight:800,
            border:'none', cursor:'pointer',
            boxShadow:'0 0 12px rgba(0,212,255,0.35)',
            transition:'all 0.2s', flexShrink:0,
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 20px rgba(0,212,255,0.6)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(0,212,255,0.35)'; e.currentTarget.style.transform = 'none'; }}
        >
          Join →
        </button>
      </div>
    </div>
  );
}

/* ── Running room card ────────────────────────────────────────── */
function RunningRoomCard({ room }) {
  const filled   = room.players?.length ?? 0;
  const total    = room.maxPlayers || 4;
  const modeMeta = MODE_COLORS[room.gameMode] || MODE_COLORS.classic;

  return (
    <div style={{
      display:'flex', flexDirection:'column', gap:10,
      background:'rgba(0,212,255,0.04)',
      border:'1px solid rgba(0,212,255,0.1)',
      borderRadius:12,
      padding:'12px 14px',
      position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'absolute', left:0, top:10, bottom:10, width:3, borderRadius:'0 3px 3px 0', background:'#00d4ff', opacity:0.5 }} />

      <div style={{ display:'flex', alignItems:'center', gap:10, paddingLeft:8 }}>
        {room.private && <span style={{ fontSize:13, flexShrink:0 }}>🔒</span>}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:'rgba(255,255,255,0.9)', fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{room.name || 'Game Room'}</div>
          <div style={{ color:'rgba(255,255,255,0.3)', fontSize:10, marginTop:1 }}>ROOM ID: {room.roomCode || room._id?.toString().slice(-8).toUpperCase()}</div>
        </div>
        <RunningBadge />
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingLeft:8, flexWrap:'wrap', gap:6 }}>
        <PlayerPips filled={filled} total={total} />
        <span style={{
          background:`${modeMeta.color}18`,
          border:`1px solid ${modeMeta.color}35`,
          borderRadius:6, padding:'2px 8px',
          fontSize:10, fontWeight:600, color:modeMeta.color,
        }}>{modeMeta.label}</span>
      </div>
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────── */
function EmptyState({ message }) {
  return (
    <div style={{
      textAlign:'center', padding:'32px 20px',
      color:'rgba(255,255,255,0.3)',
      display:'flex', flexDirection:'column', alignItems:'center', gap:10,
    }}>
      <div style={{ fontSize:36 }}>🎲</div>
      <div style={{ fontSize:14, fontWeight:600 }}>{message}</div>
      <div style={{ fontSize:12 }}>Host a room to get started!</div>
    </div>
  );
}

/* ── Section heading ──────────────────────────────────────────── */
function SectionHead({ title, count }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
      <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.8px' }}>{title}</div>
      {count > 0 && (
        <span style={{
          background:'rgba(0,212,255,0.12)', color:'#00d4ff',
          border:'1px solid rgba(0,212,255,0.25)',
          borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:700,
        }}>{count}</span>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────── */
const ServerListTable = ({ rooms = [], handleJoinClick, showRunning = true }) => {
  const waitingRooms = rooms.filter(r => !r.started);
  const runningRooms = showRunning ? rooms.filter(r => r.started) : [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* ── Available rooms ── */}
      <div>
        <SectionHead title="Available Rooms" count={waitingRooms.length} />
        {waitingRooms.length === 0 ? (
          <EmptyState message="No rooms available right now" />
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {waitingRooms.map((room, i) => (
              <WaitingRoomCard key={room._id || i} room={room} onJoin={handleJoinClick} />
            ))}
          </div>
        )}
      </div>

      {/* ── Running games ── */}
      {showRunning && runningRooms.length > 0 && (
        <div>
          <SectionHead title="Live Games" count={runningRooms.length} />
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {runningRooms.map((room, i) => (
              <RunningRoomCard key={room._id || i} room={room} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerListTable;
